import React from 'react';
import {
  useGetDefaultSettlementConfiguration,
  useWealthlaneAwardSettlementApi,
} from '../../../../../hooks';
import { ElectionCardBlock, ElectionWindowWrapper } from './electionWindow.styled';
import Heading from '../../../../../components/typography';
import { Form, Formik } from 'formik';
import Button from '../../../../../components/button';
import FormSelect from '../../../../../components/dropdown/formSelect';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import ToastComponent from '../../../../../components/toast';
import _ from 'lodash';
import Spinners from '../../../../../components/spinner';
import { SettlementConfigurationService, DefaultSettlementConfigurationService } from '@wealthlane/api-services/wealthlane-award-settlement-service';

interface SarElectionConfigProps {
  refresher: boolean;
}

const SarElectionConfig = ({ refresher }: SarElectionConfigProps) => {
  const queryClient = useQueryClient();
  const { handleRequest } = useWealthlaneAwardSettlementApi();

  const { data: sarSettlementOptions, isLoading: sarOptionsLoading } = useQuery(
    ['getSarSettlementConfiguration'],
    async () => {
      return await handleRequest(
        SettlementConfigurationService.getApiAwardSettlementApiAppSettlementConfigurationSettlementConfigurationForSar()
      );
    },
    {
      select: (data) => {
        return data?.map((item: any) => ({
          label: item.displayName ?? '',
          value: item.id ?? '',
        }));
      },
    }
  );

  const { data: defaultConfigs, isLoading: defaultConfigLoading } =
    useGetDefaultSettlementConfiguration(handleRequest);

  const sarDefaultConfig = defaultConfigs?.find(
    (config: any) => config.planTypeCategory === 'SAR'
  );

  const { mutate: mutateSar, isLoading: saveLoading } = useMutation(
    async (payload: any) => {
      return await handleRequest(
        DefaultSettlementConfigurationService.postApiAwardSettlementApiAppDefaultSettlementConfigurationSaveDefaultSettlementConfiguration(
          payload
        )
      );
    },
    {
      onSuccess: (response: any) => {
        if (response != null) {
          toast.success(<ToastComponent label='Success' message={'Configuration saved'} />);
          void queryClient.invalidateQueries('getSarSettlementConfiguration');
          void queryClient.invalidateQueries('getDefaultSettlementConfigurationList');
        }
      },
      onError: (err) => {
        console.log('error', err);
      },
    }
  );

  const handleSubmit = (values: any): void => {
    if (!values.defaultSettlementTypeId) {
      toast.error(
        <ToastComponent label='Error' message={'Please select a settlement method'} />
      );
      return;
    }

    if (sarDefaultConfig?.defaultSettlementTypeId === values.defaultSettlementTypeId) {
      toast.error(<ToastComponent label='Info' message={'No changes made'} />, {
        autoClose: 30000,
      });
      return;
    }

    const payload = {
      defaultSettlementConfigurations: [
        {
          defaultSettlementConfigurationId:
            sarDefaultConfig?.defaultSettlementConfigurationId ?? '',
          planTypeCategoryId: sarDefaultConfig?.planTypeCategoryId ?? '',
          planTypeCategory: 'SAR',
          defaultSettlementTypeId: values.defaultSettlementTypeId,
        },
      ],
    };

    mutateSar(payload);
  };

  const isLoading = sarOptionsLoading || defaultConfigLoading;

  return (
    <ElectionWindowWrapper id="sarElectionConfigWrapper">
      <div className='flex gap-x-5 mb-2' id="sarElectionConfigHeader">
        <Heading
          variant='h4'
          title={'Set Up Tax Settlement Method - SAR'}
          className='election-head'
          id="sarElectionConfigTitle"
        />
      </div>

      <ElectionCardBlock id="sarElectionConfigCardBlock">
        {isLoading && <Spinners />}
        {!isLoading && (
          <Formik
            initialValues={{
              defaultSettlementTypeId: sarDefaultConfig?.defaultSettlementTypeId ?? '',
            }}
            enableReinitialize
            onSubmit={(values) => {
              handleSubmit(values);
            }}>
            {({ touched, handleChange, errors, values, setFieldValue }) => (
              <Form id='sarElectionConfigForm'>
                <div className='grid lg:grid-cols-12 md:grid-cols-1' id="sarElectionConfigFormContainer">
                  <div className='lg:col-span-4 col-span-1' id="sarSettlementMethodsContainer">
                    <FormSelect
                      type='text'
                      label='SAR Awards'
                      name='defaultSettlementTypeId'
                      id='defaultSettlementTypeId'
                      onChange={handleChange}
                      errors={errors}
                      touched={touched}
                      options={sarSettlementOptions}
                      isLoading={sarOptionsLoading}
                      values={values}
                      arrValue={values.defaultSettlementTypeId}
                      setFieldValue={setFieldValue}
                      containerClassName='formGroup'
                      width='200px'
                      required
                    />
                  </div>
                </div>
                <div id="saveSarElectionConfigButtonContainer">
                  <Button
                    btnClassName='mt-[16px]'
                    title='Save'
                    type='submit'
                    variant='primary'
                    loading={saveLoading}
                    id="saveSarElectionConfigButton"
                  />
                </div>
              </Form>
            )}
          </Formik>
        )}
      </ElectionCardBlock>
    </ElectionWindowWrapper>
  );
};

export default SarElectionConfig;
